import { useCallback, useEffect, useRef, useState } from 'react';
import { usePatchCatalogItemMutation } from '../../../redux/api/fymAPI';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult, isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { serviceLinksWith } from './serviceLinkEdit';

/** How long a row stays marked "Linked" after a save (design d3: the highlight fades after 3 s). */
export const JUST_LINKED_MS = 3000;

export interface SaveServiceLink {
  /** Saves one link at once (`PATCH .../catalog/:uuid { serviceLinks }` with the full map). */
  save: (integrationType: string, url: string | null) => Promise<boolean>;
  /** The service whose PATCH runs now. */
  savingType: string | null;
  /** The service that got a link a moment ago (the highlight of d3). */
  justLinked: string | null;
  /** The error of the last save of a service, by service. */
  errors: Record<string, ErrorAPIResult['error']>;
  clearError: (integrationType: string) => void;
}

/**
 * The save of "Where it plays": each row saves by itself at once, on the Kit tab and on the song
 * page (design d2). The links are not in the save bar of the song page any more.
 */
export function useSaveServiceLink({ accountId, item }: { accountId: number; item: CatalogItem }): SaveServiceLink {
  const [patch] = usePatchCatalogItemMutation();
  const [savingType, setSavingType] = useState<string | null>(null);
  const [justLinked, setJustLinked] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, ErrorAPIResult['error']>>({});
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const clearError = useCallback((integrationType: string) => {
    setErrors((current) => {
      const next = { ...current };
      delete next[integrationType];
      return next;
    });
  }, []);

  const save = useCallback(
    async (integrationType: string, url: string | null): Promise<boolean> => {
      clearError(integrationType);
      setSavingType(integrationType);
      const result = await patch({
        accountId,
        itemUuid: item.uuid,
        serviceLinks: serviceLinksWith(item.serviceLinks, integrationType, url),
      });
      setSavingType(null);
      if (!isSuccessfulResult(result)) {
        setErrors((current) => ({ ...current, [integrationType]: result.error }));
        return false;
      }
      window.clearTimeout(timer.current);
      if (url !== null) {
        setJustLinked(integrationType);
        timer.current = window.setTimeout(() => setJustLinked(null), JUST_LINKED_MS);
      }
      return true;
    },
    [accountId, clearError, item.serviceLinks, item.uuid, patch]
  );

  return { save, savingType, justLinked, errors, clearError };
}
